import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/ui/button';
import { MoreVertical } from 'lucide-react';

type PrimaryAction = {
    title: string;
    onClick: (e: React.MouseEvent) => void;
    icon: React.ReactNode;
    disabled?: boolean;
    hidden?: boolean;
};

type OverflowAction = {
    label: string;
    onClick: (e: React.MouseEvent) => void;
    icon?: React.ReactNode;
    disabled?: boolean;
    destructive?: boolean;
};

interface StandardActionsProps {
    view?: PrimaryAction;
    edit?: PrimaryAction;
    download?: PrimaryAction;
    remove?: PrimaryAction;
    overflow?: OverflowAction[];
}

export const StandardActions: React.FC<StandardActionsProps> = ({
    view,
    edit,
    download,
    remove,
    overflow = []
}) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (event: MouseEvent) => {
            if (menuRef.current?.contains(event.target as Node)) return;
            if (triggerRef.current?.contains(event.target as Node)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.right - 176 }); // 176 = w-44
        }
        setOpen((prev) => !prev);
    };

    const renderButton = (action?: PrimaryAction) => {
        if (!action || action.hidden) return null;
        return (
            <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={action.onClick}
                title={action.title}
                aria-label={action.title}
                disabled={action.disabled}
            >
                {action.icon}
            </Button>
        );
    };

    return (
        <div className="flex gap-1 justify-end items-center">
            {renderButton(view)}
            {renderButton(edit)}
            {renderButton(download)}
            {renderButton(remove)}
            {overflow.length > 0 && (
                <>
                    <Button
                        ref={triggerRef}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleToggle}
                        title="More actions"
                    >
                        <MoreVertical className="w-4 h-4 text-slate-600" />
                    </Button>
                    {open && createPortal(
                        <div
                            ref={menuRef}
                            className="fixed w-44 rounded-md border bg-white shadow-md z-50"
                            style={{ top: menuPos.top, left: menuPos.left }}
                        >
                            {overflow.map((action, idx) => (
                                <button
                                    key={`${action.label}-${idx}`}
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${action.destructive ? 'text-red-600' : 'text-slate-700'
                                        }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (action.disabled) return;
                                        setOpen(false);
                                        action.onClick(e);
                                    }}
                                    disabled={action.disabled}
                                >
                                    {action.icon}
                                    <span>{action.label}</span>
                                </button>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};
